import axios, { AxiosInstance, AxiosRequestConfig, CreateAxiosDefaults } from 'axios';
import axiosRetry, { IAxiosRetryConfig } from 'axios-retry';

type RetryableRequestConfig = AxiosRequestConfig & { originalUrl?: string };

interface HttpClientConfig {
  axiosConfig?: CreateAxiosDefaults;
  retryConfig?: IAxiosRetryConfig;
}

/**
 * Creates Axios clients with centrally configured retries.
 * Retries use exponential backoff and, by default, only cover network errors
 * and idempotent requests failing with 5xx (axios-retry's safe-to-retry check).
 * Once retries are exhausted the original error is rejected as usual.
 */
export class AxiosHttpClient {
  static create({ axiosConfig = {}, retryConfig = {} }: HttpClientConfig = {}): AxiosInstance {
    const instance = axios.create(axiosConfig);
    // Remember the URL as first requested so a retry can never resend an already-extended query string
    instance.interceptors.request.use((config) => {
      const retryableConfig: RetryableRequestConfig = config;
      retryableConfig.originalUrl ??= config.url;
      return config;
    });
    axiosRetry(instance, {
      retries: 3,
      retryDelay: (retryCount) => axiosRetry.exponentialDelay(retryCount, undefined, 1000),
      onRetry: (_retryCount, _error, requestConfig: RetryableRequestConfig) => {
        requestConfig.url = requestConfig.originalUrl ?? requestConfig.url;
      },
      ...retryConfig,
    });
    return instance;
  }
}
